import { isDefined } from '@sgme/fp';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoSetIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectAlgoErrors, selectTradeIdFromAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import { selectOrderEntryByIdFromBlotter } from '@/store/orders/fixing/selectors.ts';

type AlgoLimitPriceProps = {
  limitPrice: number | undefined;
  setLimitPrice: (value: number | undefined) => void;
};

export const AlgoEditingLimitPrice = (props: AlgoLimitPriceProps) => {
  const { limitPrice, setLimitPrice } = props;
  const tileId = useAlgoTileId();
  const setIsModifyingField = useAlgoSetIsModifyingField();
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const limitPriceError = errors?.limitPrice;

  const validateAlgo = useValidateAlgo();

  const handleLimitPriceChange = async (value: number | undefined) => {
    track('Order action', 'Limit price changed');
    if (isDefined(orderFromBlotter)) {
      const response = await validateAlgo({
        limitPrice: value,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setLimitPrice(value);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.NoWorseThan">
      <OmsFieldErrorWrapper errors={limitPriceError}>
        <SgNumberInput
          valuePosition="start"
          size="sm"
          maxDecimals={9}
          value={
            limitPrice ??
            (isDefined(orderFromBlotter?.limitPrice) ? (orderFromBlotter?.limitPrice as number) : undefined)
          }
          data-e2e="no-worse-than"
          onChange={handleLimitPriceChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          selectAllOnFocus
          state={isDefined(limitPriceError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
